// The timing policy shared by every brokered device-code flow (FD-530).
//
// Extracted from `useDeviceCodeActivation` when the tenant-application flows
// (FD-527 / FD-529) became the second and third consumers of the same shape.
// Only the PURE policy moves — the constants and the two functions over them.
// Each hook keeps its own chain, and that split is deliberate in both
// directions:
//
//   · The policy is shared because two copies of a poll interval drift, and
//     here the drift is not cosmetic. Both flows perform an irreversible act in
//     a customer's tenant on their first non-pending response.
//
//   · The CHAIN is not shared, because generalising the wave-activation state
//     machine would mean refactoring the console's highest-consequence path —
//     the one that mints a tenant-join credential — in the same change that
//     adds a new page. Each hook instead carries its own at-most-one-in-flight
//     regression test, which is the property that actually matters and which a
//     shared implementation would only have given once.
//
// ── THE POLL IS A SETTLE-THEN-SCHEDULE CHAIN, NOT AN INTERVAL ───────────────
//
// Every poll is a real POST that ACTS on its first non-pending response. While
// the operator is still signing in it returns 409 AUTHORIZATION_PENDING almost
// immediately — but the first attempt after they finish does the whole job
// inside that one request: redeem, validate claims, create the vault
// certificate, write to Graph, stamp the row. A `setInterval` firing during
// that window issues a SECOND request while the first is still working.
//
// For wave activation that mints two `package_<pid>` users, one of them live
// and orphaned with no pid recorded to revoke it (FD-185's class of defect,
// arriving from the client). For tenant-application creation it is the same
// shape with a different object: TWO APPLICATION REGISTRATIONS IN A CUSTOMER'S
// DIRECTORY, one of which the registry does not name — and which AirBridge,
// having refused `Application.ReadWrite.OwnedBy`, has no standing authority to
// clean up (`FD-ADR-046` D1).
//
// Because the next timeout is only ever armed from the settle handler, "at most
// one request in flight" is a property of the shape rather than of care.

/** Never poll faster than this, whatever the server suggests. */
export const MIN_POLL_MS = 5_000;
/** Never poll slower than this, whatever the server suggests. */
export const MAX_POLL_MS = 60_000;
/** Hard stop on attempts, independent of the deadline. */
export const MAX_ATTEMPTS = 240;
/** Countdown repaint. DISPLAY ONLY — this timer never issues a request. */
export const TICK_MS = 1_000;

export function clamp(ms: number): number {
  return Math.min(MAX_POLL_MS, Math.max(MIN_POLL_MS, ms));
}

/**
 * There is deliberately no client-side SLOW_DOWN step. The Manager already
 * translates OAuth `slow_down` into a larger `retryAfterSeconds`, so the
 * escalation lives server-side where the OAuth error actually arrives. The
 * client's rule — take the MAXIMUM of the current interval and the server's
 * suggestion — is what makes that escalation permanent: the interval can rise
 * and never falls back.
 */
export function nextInterval(current: number, suggestedSeconds: number | undefined): number {
  if (suggestedSeconds === undefined) return current;
  return clamp(Math.max(current, suggestedSeconds * 1_000));
}

/** Whole seconds remaining until an absolute deadline, floored at zero. */
export function secondsLeft(deadlineMs: number): number {
  return Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1_000));
}
